/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

// @ts-ignore
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { TContextProvider } from '../../../interfaces';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * CartSpecificProductTrigger class - proactive chat is triggered if a Product in the Cart is in preset list of products (config.proactiveOnCartSpecificProductList)
 */
class CartSpecificProductTrigger extends ProactiveChatTrigger {
    private product: string = '';

    public async validate(): Promise<boolean> {
        let isValid = false;
        const cart = await this._getCart();
        if (!cart.isEmpty) {
            if (cart.cart.CartLines) {
                this.props.context.telemetry.debug(
                    `[${CartSpecificProductTrigger.name}] Number of cart line items was '${cart.cart.CartLines?.length}'`
                );
                for (let i = 0; i < cart.cart.CartLines?.length; i++) {
                    if (cart.cart.CartLines[i]) {
                        if (this.props.config.proactiveOnCartSpecificProductList) {
                            this.props.context.telemetry.debug(
                                `[${CartSpecificProductTrigger.name}] Number of cart specific products was configured for proactive chat was '${this.props.config.proactiveOnCartSpecificProductList?.length}'`
                            );
                            // Find first item, from user-defined list of products, that matches the cart lines' itemId in the cart.
                            for (let j = 0; j < (this.props.config.proactiveOnCartSpecificProductList?.length || 0); j++) {
                                if (
                                    this.props.config.proactiveOnCartSpecificProductList &&
                                    cart.cart.CartLines[i].ItemId &&
                                    this.props.config.proactiveOnCartSpecificProductList[j] === cart.cart.CartLines[i].ItemId
                                ) {
                                    this.props.context.telemetry.debug(
                                        `[${CartSpecificProductTrigger.name}] Matched cart line item ID '${cart.cart.CartLines[i].ItemId}' against user-defined list of specific products for proactive chat.`
                                    );
                                    isValid = true;
                                    break;
                                }
                            }
                        }
                    } else {
                        // Rest of the CartLines are undefined
                        break;
                    }
                }
            } else {
                this.props.context.telemetry.debug(`[Chat][${CartSpecificProductTrigger.name}] Cart did not contain any cart lines.`);
            }
        } else {
            this.props.context.telemetry.debug(`[Chat][${CartSpecificProductTrigger.name}] Cart was empty.`);
        }
        this.props.context.telemetry.debug(`[Chat][${CartSpecificProductTrigger.name}] Validation returning '${isValid}'.`);
        return isValid;
    }

    public getContextProvider(): TContextProvider {
        return () => {
            return {
                ...this.context,
                eCommerceCustIsProactiveChat: 'True',
                eCommerceCustProactiveType: 'Cart Specific Product',
                eCommerceCustProactiveShoppingCartSpecificProduct: `${this.product}`
            };
        };
    }

    public getMessage(): string {
        // @TODO: Add message processing if needed
        return this.props.config.proactiveOnCartSpecificProductMessage || this.props.config.proactiveDefaultMessage;
    }

    private async _getCart(): Promise<ICartState> {
        return getCartState(this.props.context.actionContext);
    }
}

export default CartSpecificProductTrigger;
